import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../app/hooks";
import { FormProvider, useForm } from "react-hook-form";
import { createCustomerOrganization } from "../../customerOrganizationsSlice";
import { isFulfilled } from "@reduxjs/toolkit";
import DialogComponent from "../../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../../common/components/functional/dialogs/DialogContentWrapper";
import { CreateOrganizationForm } from "./CreateOrganizationForm";
import { useEffect } from "react";
import useIsLoading from "../../../../hooks/useIsLoading";

type Props = {
  customerCompanyId: string;
  open: boolean;
  onClose: () => void;
};

export const CreateOrganizationDialog = ({
  customerCompanyId,
  open,
  onClose: handleClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const isLoading = useIsLoading(["customerOrganizations"]);
  const { t } = useTranslation("customers");

  const defaultValues = {
    businessId: "",
    businessIdDisabled: false,
    name: "",
    customerNumber: "",
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    formState: { isValid, isSubmitting },
  } = methods;

  const businessIdDisabled = methods.watch("businessIdDisabled");

  useEffect(() => {
    if (businessIdDisabled) {
      methods.setValue("businessId", "");
    }
    methods.trigger("businessId");
  }, [businessIdDisabled, methods]);

  const saveDisabled = !isValid || isSubmitting || isLoading;

  const onClose = () => {
    methods.reset();
    handleClose();
  };

  const onSave = (data: any) => {
    const payload = {
      companyId: customerCompanyId,
      name: data.name,
      customerNumber: data.customerNumber,
      businessId: data.businessIdDisabled ? null : data.businessId,
    };
    dispatch(createCustomerOrganization(payload)).then((action) => {
      if (isFulfilled(action)) {
        onClose();
      }
    });
  };

  return (
    <DialogComponent open={open} onClose={onClose} sx={{ maxWidth: "550px" }}>
      <FormProvider {...methods}>
        <DialogContentWrapper
          title={t("suborganizationInfo")}
          onSave={methods.handleSubmit(onSave)}
          onCancel={onClose}
          saveDisabled={saveDisabled}
          customSaveButtonLabel={t("common:save")}
        >
          <CreateOrganizationForm />
        </DialogContentWrapper>
      </FormProvider>
    </DialogComponent>
  );
};
